/**
 * Expense management panel (BA-06).
 * Full CRUD: list with search + category filter, add, edit, delete.
 * Extracted from AnalyticsPage so the page stays focused on KPIs/charts.
 */
import type { ExpenseCategory } from '@gym/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2, Wallet } from 'lucide-react';
import { Plus } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IconButton, Button } from '../../components/ui/Button.js';
import { ExpandableCard } from '../../components/ui/ExpandableCard.js';
import { EmptyState } from '../../components/ui/Feedback.js';
import { Field, Input, Select } from '../../components/ui/Field.js';
import { SearchInput } from '../../components/ui/Layout.js';
import { Modal, ConfirmModal } from '../../components/ui/Modal.js';
import { Money, useFormatters } from '../../components/ui/Money.js';
import { Chip } from '../../components/ui/StatusPill.js';
import { Table, TableSkeleton, TableWrap, TD, TH, THead, TR } from '../../components/ui/Table.js';
import { toast } from '../../components/ui/Toaster.js';
import { supabase } from '../../lib/supabase.js';

// ── Constants ──────────────────────────────────────────────────────────────────

const CATEGORIES: ExpenseCategory[] = [
  'rent',
  'salary',
  'utilities',
  'equipment',
  'marketing',
  'supplies',
  'tax',
  'other',
];

// ── Types ──────────────────────────────────────────────────────────────────────

interface ExpenseRow {
  id: string;
  category: string;
  amount: number;
  description: string | null;
  incurred_on: string;
}

// ── ExpenseOverview (exported) ─────────────────────────────────────────────────

export function ExpenseOverview({ tenantId }: { tenantId: string }): ReactNode {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const qc = useQueryClient();

  // ── Local state ──
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<'all' | ExpenseCategory>('all');
  const [formTarget, setFormTarget] = useState<ExpenseRow | null | 'new'>(null);
  const [deleteTarget, setDeleteTarget] = useState<ExpenseRow | null>(null);

  // ── Query ──
  const expenses = useQuery({
    queryKey: ['expenses', tenantId],
    enabled: !!tenantId,
    queryFn: async (): Promise<ExpenseRow[]> => {
      const { data, error } = await supabase
        .from('expenses')
        .select('id, category, amount, description, incurred_on')
        .order('incurred_on', { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as unknown as ExpenseRow[];
    },
  });

  // ── Delete mutation ──
  const doDelete = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('expenses').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t('analytics.expense.delete'));
      void qc.invalidateQueries({ queryKey: ['expenses'] });
      void qc.invalidateQueries({ queryKey: ['profit_loss'] });
      setDeleteTarget(null);
    },
    onError: (e: Error) => toast.error(t('common.error'), e.message),
  });

  // ── Client-side filtering ──
  const filtered = useMemo(() => {
    const rows = expenses.data ?? [];
    const q = search.toLowerCase();
    return rows.filter((r) => {
      if (categoryFilter !== 'all' && r.category !== categoryFilter) return false;
      if (!q) return true;
      const catLabel = t(`analytics.expenseCategory.${r.category}`, {
        defaultValue: r.category,
      }).toLowerCase();
      const desc = (r.description ?? '').toLowerCase();
      const amount = String(r.amount);
      return catLabel.includes(q) || desc.includes(q) || amount.includes(q);
    });
  }, [expenses.data, search, categoryFilter, t]);

  return (
    <>
      <ExpandableCard title={t('analytics.expense.title')}>
        {/* Filters */}
        <div className="mb-3 flex flex-wrap items-center justify-end gap-2">
          <SearchInput
            placeholder={t('analytics.expense.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-48"
          />
          <Select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value as 'all' | ExpenseCategory)}
            className="w-40"
          >
            <option value="all">{t('common.all')}</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {t(`analytics.expenseCategory.${c}`, { defaultValue: c })}
              </option>
            ))}
          </Select>
          <Button
            variant="brand"
            size="sm"
            icon={<Plus className="h-4 w-4" />}
            onClick={() => setFormTarget('new')}
          >
            {t('analytics.addExpense')}
          </Button>
        </div>

        {/* Table */}
        <TableWrap>
          <Table>
            <THead>
              <tr>
                <TH>{t('common.date')}</TH>
                <TH>{t('analytics.category')}</TH>
                <TH>{t('analytics.description')}</TH>
                <TH numeric>{t('billing.amount')}</TH>
                <TH>{t('common.actions')}</TH>
              </tr>
            </THead>
            {expenses.isLoading ? (
              <TableSkeleton rows={6} cols={5} />
            ) : (
              <tbody>
                {filtered.map((e) => (
                  <TR key={e.id}>
                    <TD className="text-muted">{fmt.date(e.incurred_on)}</TD>
                    <TD>
                      <Chip tone="neutral">
                        {t(`analytics.expenseCategory.${e.category}`, {
                          defaultValue: e.category,
                        })}
                      </Chip>
                    </TD>
                    <TD className="text-muted">{e.description ?? '—'}</TD>
                    <TD numeric>
                      <Money minor={e.amount} />
                    </TD>
                    <TD>
                      <div className="flex items-center gap-1">
                        <IconButton
                          label={t('analytics.expense.edit')}
                          onClick={() => setFormTarget(e)}
                        >
                          <Pencil className="h-4 w-4" />
                        </IconButton>
                        <IconButton
                          label={t('analytics.expense.delete')}
                          variant="ghost"
                          onClick={() => setDeleteTarget(e)}
                        >
                          <Trash2 className="h-4 w-4 text-danger" />
                        </IconButton>
                      </div>
                    </TD>
                  </TR>
                ))}
              </tbody>
            )}
          </Table>
        </TableWrap>

        {/* Empty state */}
        {!expenses.isLoading && filtered.length === 0 ? (
          <div className="p-6">
            <EmptyState icon={Wallet} title={t('analytics.expense.empty')} />
          </div>
        ) : null}
      </ExpandableCard>

      {/* Add / Edit modal */}
      {formTarget === 'new' ? (
        <ExpenseFormModal tenantId={tenantId} onClose={() => setFormTarget(null)} />
      ) : formTarget !== null ? (
        <ExpenseFormModal
          tenantId={tenantId}
          expense={formTarget}
          onClose={() => setFormTarget(null)}
        />
      ) : null}

      {/* Delete confirm modal */}
      {deleteTarget !== null ? (
        <ConfirmModal
          open
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title={t('analytics.expense.confirmDelete')}
          confirmLabel={t('common.delete')}
          cancelLabel={t('common.cancel')}
          destructive
          loading={doDelete.isPending}
          onConfirm={() => doDelete.mutate(deleteTarget.id)}
        />
      ) : null}
    </>
  );
}

// ── ExpenseFormModal (create + edit) ──────────────────────────────────────────

function ExpenseFormModal({
  tenantId,
  expense,
  onClose,
}: {
  tenantId: string;
  expense?: ExpenseRow;
  onClose: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const isEdit = !!expense;

  const [category, setCategory] = useState<ExpenseCategory>(
    (expense?.category as ExpenseCategory) ?? 'rent',
  );
  const [amountMajor, setAmountMajor] = useState(expense ? String(expense.amount / 100) : '');
  const [description, setDescription] = useState(expense?.description ?? '');
  const [date, setDate] = useState(expense?.incurred_on ?? new Date().toISOString().slice(0, 10));

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        category,
        amount: Math.round(Number(amountMajor) * 100),
        description: description.trim() || null,
        incurred_on: date,
      };

      if (isEdit) {
        const { error } = await supabase.from('expenses').update(payload).eq('id', expense.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('expenses').insert({
          tenant_id: tenantId,
          ...payload,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success(t(isEdit ? 'analytics.expense.edit' : 'analytics.expenseAdded'));
      void qc.invalidateQueries({ queryKey: ['expenses'] });
      void qc.invalidateQueries({ queryKey: ['profit_loss'] });
      onClose();
    },
    onError: (e: Error) => toast.error(t('common.error'), e.message),
  });

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={t(isEdit ? 'analytics.expense.edit' : 'analytics.addExpense')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!amountMajor}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="grid gap-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('analytics.category')}>
            {(id) => (
              <Select
                id={id}
                value={category}
                onChange={(e) => setCategory(e.target.value as ExpenseCategory)}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {t(`analytics.expenseCategory.${c}`, { defaultValue: c })}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={`${t('billing.amount')} (${t('common.currency')})`} required>
            {(id) => (
              <Input
                id={id}
                type="number"
                min="0"
                value={amountMajor}
                onChange={(e) => setAmountMajor(e.target.value)}
              />
            )}
          </Field>
        </div>
        <Field label={t('common.date')}>
          {(id) => (
            <Input id={id} type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          )}
        </Field>
        <Field label={t('analytics.description')}>
          {(id) => (
            <Input id={id} value={description} onChange={(e) => setDescription(e.target.value)} />
          )}
        </Field>
      </div>
    </Modal>
  );
}
